import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { ContactProfile } from '../contacts/types'
import { invalidateQueries, updateToast } from '../helpers'
import { createUpdateContactProfile, deleteContactProfile, getContactProfileById, getContactProfiles } from './functions'
import {
    ContactProfileListPayload,
    ContactProfileMutationPayload,
    ContactProfileMutationResponse,
    ContactProfilePayload,
    ContactProfileResponse,
} from './types'

const contactProfiles = {
    GetContactProfile: {
        useQuery: (params: ContactProfilePayload, options?: UseQueryOptions<ContactProfile, AxiosError>) =>
            useQuery({
                queryKey: ['contactProfile', params.contactId, params.profileId],
                queryFn: () => getContactProfileById(params),
                enabled: Boolean(params.contactId && params.profileId),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    GetContactProfiles: {
        useQuery: (params: ContactProfileListPayload, options?: UseQueryOptions<ContactProfileResponse, AxiosError>) => {
            return useQuery({
                queryKey: ['contactProfile', 'list', params],
                queryFn: () => getContactProfiles(params),
                enabled: Boolean(params?.contactId),
                staleTime: 1 * 60 * 1000,
                ...options,
            })
        },
    },
    CreateUpdateContactProfile: {
        useMutation: (options?: UseMutationOptions<ContactProfileMutationResponse, AxiosError, ContactProfileMutationPayload>) =>
            useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading.contactProfile') as string)
                    return toastId
                },
                mutationFn: (payload: ContactProfileMutationPayload) => createUpdateContactProfile(payload),
                onSuccess: (data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.contactProfile.success') as string, 'success')

                    if (variables.isEdit && variables.profileId) {
                        invalidateQueries(['contactProfile', variables.contactId, variables.profileId.toString()])
                    }

                    invalidateQueries(['contactProfile', 'list'])
                    invalidateQueries(['properties', 'list', 'opportunity'])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.contactProfile.error') as string, 'error')
                },
                ...options,
            }),
    },
    DeleteContactProfile: {
        useMutation: (options?: UseMutationOptions<ContactProfileMutationResponse, AxiosError, ContactProfilePayload>) =>
            useMutation({
                onMutate: (params: ContactProfilePayload) => {
                    toast.loading(i18n.t('toast.loading') as string, { toastId: params.profileId })
                },
                mutationFn: (params: ContactProfilePayload) => deleteContactProfile(params),
                onSuccess: (data, params) => {
                    updateToast(params.profileId, i18n.t('toast.update.success') as string, 'success')

                    invalidateQueries(['contactProfile', params.profileId.toString()])
                    invalidateQueries(['contactProfile', 'list'])
                    invalidateQueries(['properties', 'list', 'opportunity'])
                },
                onError: (error, params) => {
                    updateToast(params.profileId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            }),
    },
}

export default contactProfiles
